'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import AppLayout from '@/components/AppLayout';
import Icon from '@/components/ui/AppIcon';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend
} from 'recharts';

interface ConsumoEnergetico {
  id: string;
  device_id: string | null;
  reparto_id: string | null;
  zona_id: string | null;
  kwh: number;
  periodo_inizio: string;
  periodo_fine: string;
  costo_euro: number;
  note: string;
  zona?: { id: string; nome: string };
  reparto?: { id: string; nome: string };
  device?: { id: string; hostname: string };
}

const COLORS = ['#38bdf8', '#34d399', '#f97316', '#a78bfa', '#fb7185', '#fbbf24', '#6ee7b7'];

// Create supabase client once outside the component to avoid infinite re-render
const supabase = createClient();

export default function ConsumiEnergeticiContent() {
  const [consumi, setConsumi] = useState<ConsumoEnergetico[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'zona' | 'reparto' | 'lista'>('zona');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editItem, setEditItem] = useState<ConsumoEnergetico | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [zone, setZone] = useState<{ id: string; nome: string }[]>([]);
  const [reparti, setReparti] = useState<{ id: string; nome: string }[]>([]);
  const [devices, setDevices] = useState<{ id: string; hostname: string }[]>([]);
  const [form, setForm] = useState({ zona_id: '', reparto_id: '', device_id: '', kwh: '', costo_euro: '', periodo_inizio: '', periodo_fine: '', note: '' });
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [consumiRes, zoneRes, repartiRes, devicesRes] = await Promise.all([
        supabase.from('consumi_energetici').select('*, zona:zone_fisiche(id,nome), reparto:reparti(id,nome), device:devices(id,hostname)').order('periodo_inizio', { ascending: false }),
        supabase.from('zone_fisiche').select('id,nome').order('nome'),
        supabase.from('reparti').select('id,nome').order('nome'),
        supabase.from('devices').select('id,hostname').order('hostname').limit(100),
      ]);
      if (consumiRes.error) throw consumiRes.error;
      if (zoneRes.error) throw zoneRes.error;
      if (repartiRes.error) throw repartiRes.error;
      if (devicesRes.error) throw devicesRes.error;
      setConsumi(consumiRes.data || []);
      setZone(zoneRes.data || []);
      setReparti(repartiRes.data || []);
      setDevices(devicesRes.data || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openAdd = () => {
    setEditItem(null);
    setForm({ zona_id: '', reparto_id: '', device_id: '', kwh: '', costo_euro: '', periodo_inizio: '', periodo_fine: '', note: '' });
    setShowAddForm(true);
  };

  const openEdit = (c: ConsumoEnergetico) => {
    setEditItem(c);
    setForm({
      zona_id: c.zona_id || '',
      reparto_id: c.reparto_id || '',
      device_id: c.device_id || '',
      kwh: String(c.kwh),
      costo_euro: String(c.costo_euro),
      periodo_inizio: c.periodo_inizio ? c.periodo_inizio.slice(0, 10) : '',
      periodo_fine: c.periodo_fine ? c.periodo_fine.slice(0, 10) : '',
      note: c.note || '',
    });
    setShowAddForm(true);
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    const kwh = parseFloat(form.kwh);
    if (isNaN(kwh) || kwh <= 0) { setError('kWh deve essere maggiore di 0'); return; }
    const costo = parseFloat(form.costo_euro) || 0;
    if (costo < 0) { setError('Costo non può essere negativo'); return; }
    if (!form.periodo_inizio || !form.periodo_fine) { setError('Periodo inizio e fine obbligatori'); return; }
    if (new Date(form.periodo_fine) < new Date(form.periodo_inizio)) { setError('Periodo fine deve essere dopo periodo inizio'); return; }
    setSaving(true);
    try {
      const payload = {
        zona_id: form.zona_id || null,
        reparto_id: form.reparto_id || null,
        device_id: form.device_id || null,
        kwh,
        costo_euro: costo,
        periodo_inizio: form.periodo_inizio,
        periodo_fine: form.periodo_fine,
        note: form.note,
      };
      if (editItem) {
        const { error: err } = await supabase.from('consumi_energetici').update(payload).eq('id', editItem.id);
        if (err) throw err;
      } else {
        const { error: err } = await supabase.from('consumi_energetici').insert(payload);
        if (err) throw err;
      }
      setShowAddForm(false);
      setEditItem(null);
      setForm({ zona_id: '', reparto_id: '', device_id: '', kwh: '', costo_euro: '', periodo_inizio: '', periodo_fine: '', note: '' });
      fetchData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error: err } = await supabase.from('consumi_energetici').delete().eq('id', id);
      if (err) throw err;
      setDeleteConfirmId(null);
      fetchData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  // Aggregazione per zona
  const consumiPerZona = zone.map(z => ({
    nome: z.nome,
    kwh: consumi.filter(c => c.zona_id === z.id).reduce((sum, c) => sum + c.kwh, 0),
    costo: consumi.filter(c => c.zona_id === z.id).reduce((sum, c) => sum + c.costo_euro, 0),
  })).filter(z => z.kwh > 0);

  // Aggregazione per reparto
  const consumiPerReparto = reparti.map(r => ({
    nome: r.nome,
    kwh: consumi.filter(c => c.reparto_id === r.id).reduce((sum, c) => sum + c.kwh, 0),
    costo: consumi.filter(c => c.reparto_id === r.id).reduce((sum, c) => sum + c.costo_euro, 0),
  })).filter(r => r.kwh > 0);

  const totalKwh = consumi.reduce((sum, c) => sum + c.kwh, 0);
  const totalCosto = consumi.reduce((sum, c) => sum + c.costo_euro, 0);

  if (loading) return (
    <AppLayout>
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
      </div>
    </AppLayout>
  );

  return (
    <AppLayout>
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Consumi Energetici</h1>
            <p className="text-muted-color text-sm mt-1">Monitoraggio kWh per dispositivo e reparto</p>
          </div>
          <div className="flex gap-2">
            <button onClick={fetchData} className="flex items-center gap-2 px-4 py-2 bg-sky-500/10 text-sky-400 rounded-lg hover:bg-sky-500/20 transition-colors text-sm font-semibold">
              <Icon name="ArrowPathIcon" size={16} />
              Aggiorna
            </button>
            <button onClick={openAdd} className="flex items-center gap-2 px-4 py-2 bg-sky-500 text-white rounded-lg hover:bg-sky-600 transition-colors text-sm font-semibold">
              <Icon name="PlusIcon" size={16} />
              Aggiungi
            </button>
          </div>
        </div>

        {error && <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-400 text-sm">{error}</div>}

        {/* KPI */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-surface rounded-xl p-4 border border-border">
            <p className="text-muted-color text-xs mb-1">Consumo Totale</p>
            <p className="text-2xl font-bold text-white">{totalKwh.toFixed(1)} <span className="text-sm text-muted-color">kWh</span></p>
          </div>
          <div className="bg-surface rounded-xl p-4 border border-border">
            <p className="text-muted-color text-xs mb-1">Costo Totale</p>
            <p className="text-2xl font-bold text-amber-400">€{totalCosto.toFixed(2)}</p>
          </div>
          <div className="bg-surface rounded-xl p-4 border border-border">
            <p className="text-muted-color text-xs mb-1">Zone Monitorate</p>
            <p className="text-2xl font-bold text-white">{consumiPerZona.length}</p>
          </div>
          <div className="bg-surface rounded-xl p-4 border border-border">
            <p className="text-muted-color text-xs mb-1">Reparti Monitorati</p>
            <p className="text-2xl font-bold text-white">{consumiPerReparto.length}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-surface rounded-lg p-1 w-fit border border-border">
          {(['zona', 'reparto', 'lista'] as const).map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${activeTab === tab ? 'bg-sky-500 text-white' : 'text-muted-color hover:text-white'}`}>
              {tab === 'zona' ? 'Per Zona' : tab === 'reparto' ? 'Per Reparto' : 'Lista Completa'}
            </button>
          ))}
        </div>

        {/* Per Zona */}
        {activeTab === 'zona' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-surface rounded-xl border border-border p-5">
              <h3 className="text-white font-semibold mb-4">Consumo kWh per Zona</h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={consumiPerZona}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis dataKey="nome" tick={{ fill: '#64748b', fontSize: 11 }} />
                  <YAxis tick={{ fill: '#64748b', fontSize: 11 }} />
                  <Tooltip contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #1e293b', borderRadius: '8px', color: '#fff' }} />
                  <Bar dataKey="kwh" name="kWh" fill="#38bdf8" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-surface rounded-xl border border-border p-5">
              <h3 className="text-white font-semibold mb-4">Distribuzione Costi per Zona</h3>
              {consumiPerZona.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie data={consumiPerZona} dataKey="costo" nameKey="nome" cx="50%" cy="50%" outerRadius={100} label={({ nome, percent }) => `${nome} ${(percent * 100).toFixed(0)}%`}>
                      {consumiPerZona.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #1e293b', borderRadius: '8px', color: '#fff' }} formatter={(v: any) => `€${Number(v).toFixed(2)}`} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-64 text-muted-color">Nessun dato</div>
              )}
            </div>
            <div className="lg:col-span-2 bg-surface rounded-xl border border-border overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left text-xs font-semibold text-muted-color px-4 py-3">ZONA</th>
                    <th className="text-left text-xs font-semibold text-muted-color px-4 py-3">CONSUMO (kWh)</th>
                    <th className="text-left text-xs font-semibold text-muted-color px-4 py-3">COSTO (€)</th>
                    <th className="text-left text-xs font-semibold text-muted-color px-4 py-3">COSTO/kWh</th>
                  </tr>
                </thead>
                <tbody>
                  {consumiPerZona.map((z, i) => (
                    <tr key={i} className="border-b border-border hover:bg-surface-elevated transition-colors">
                      <td className="px-4 py-3 text-white text-sm font-semibold">{z.nome}</td>
                      <td className="px-4 py-3 text-sky-400 text-sm font-bold">{z.kwh.toFixed(1)}</td>
                      <td className="px-4 py-3 text-amber-400 text-sm font-bold">€{z.costo.toFixed(2)}</td>
                      <td className="px-4 py-3 text-muted-color text-sm">€{z.kwh > 0 ? (z.costo / z.kwh).toFixed(3) : '—'}</td>
                    </tr>
                  ))}
                  {consumiPerZona.length === 0 && (
                    <tr><td colSpan={4} className="px-4 py-8 text-center text-muted-color text-sm">Nessun dato per zona</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Per Reparto */}
        {activeTab === 'reparto' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-surface rounded-xl border border-border p-5">
              <h3 className="text-white font-semibold mb-4">Consumo kWh per Reparto</h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={consumiPerReparto}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis dataKey="nome" tick={{ fill: '#64748b', fontSize: 11 }} />
                  <YAxis tick={{ fill: '#64748b', fontSize: 11 }} />
                  <Tooltip contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #1e293b', borderRadius: '8px', color: '#fff' }} />
                  <Bar dataKey="kwh" name="kWh" fill="#34d399" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-surface rounded-xl border border-border p-5">
              <h3 className="text-white font-semibold mb-4">Distribuzione Costi per Reparto</h3>
              {consumiPerReparto.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie data={consumiPerReparto} dataKey="costo" nameKey="nome" cx="50%" cy="50%" outerRadius={100}>
                      {consumiPerReparto.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #1e293b', borderRadius: '8px', color: '#fff' }} formatter={(v: any) => `€${Number(v).toFixed(2)}`} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-64 text-muted-color">Nessun dato</div>
              )}
            </div>
          </div>
        )}

        {/* Lista Completa */}
        {activeTab === 'lista' && (
          <div className="bg-surface rounded-xl border border-border overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left text-xs font-semibold text-muted-color px-4 py-3">PERIODO</th>
                  <th className="text-left text-xs font-semibold text-muted-color px-4 py-3">ZONA / REPARTO</th>
                  <th className="text-left text-xs font-semibold text-muted-color px-4 py-3">kWh</th>
                  <th className="text-left text-xs font-semibold text-muted-color px-4 py-3">COSTO (€)</th>
                  <th className="text-left text-xs font-semibold text-muted-color px-4 py-3">NOTE</th>
                  <th className="text-left text-xs font-semibold text-muted-color px-4 py-3">AZIONI</th>
                </tr>
              </thead>
              <tbody>
                {consumi.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-color text-sm">Nessun consumo registrato</td></tr>
                ) : consumi.map((c, i) => (
                  <tr key={c.id} className={`border-b border-border hover:bg-surface-elevated transition-colors ${i % 2 !== 0 ? 'bg-surface-elevated/20' : ''}`}>
                    <td className="px-4 py-3 text-sm text-white">
                      {c.periodo_inizio ? new Date(c.periodo_inizio).toLocaleDateString('it-IT') : '—'} → {c.periodo_fine ? new Date(c.periodo_fine).toLocaleDateString('it-IT') : '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-secondary-color">
                      {(c.zona as any)?.nome || (c.reparto as any)?.nome || (c.device as any)?.hostname || '—'}
                    </td>
                    <td className="px-4 py-3 text-sky-400 text-sm font-bold">{c.kwh.toFixed(1)}</td>
                    <td className="px-4 py-3 text-amber-400 text-sm font-bold">€{c.costo_euro.toFixed(2)}</td>
                    <td className="px-4 py-3 text-muted-color text-xs">{c.note || '—'}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <button onClick={() => openEdit(c)} className="p-1.5 rounded-lg hover:bg-sky-500/10 text-muted-color hover:text-sky-400 transition-colors" title="Modifica">
                          <Icon name="PencilIcon" size={14} />
                        </button>
                        <button onClick={() => setDeleteConfirmId(c.id)} className="p-1.5 rounded-lg hover:bg-red-500/10 text-muted-color hover:text-red-400 transition-colors" title="Elimina">
                          <Icon name="TrashIcon" size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Delete Confirm */}
        {deleteConfirmId && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-surface rounded-xl border border-border p-6 w-full max-w-sm shadow-2xl">
              <h3 className="text-lg font-semibold text-white mb-2">Elimina Consumo</h3>
              <p className="text-secondary-color text-sm mb-5">Sei sicuro di voler eliminare questo record?</p>
              <div className="flex gap-3">
                <button onClick={() => setDeleteConfirmId(null)} className="flex-1 px-4 py-2 border border-border text-secondary-color rounded-lg hover:bg-surface-elevated transition-colors text-sm">Annulla</button>
                <button onClick={() => handleDelete(deleteConfirmId)} className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm font-semibold">Elimina</button>
              </div>
            </div>
          </div>
        )}

        {/* Add/Edit Form Modal */}
        {showAddForm && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-surface rounded-xl border border-border p-6 w-full max-w-lg shadow-2xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">{editItem ? 'Modifica Consumo' : 'Aggiungi Consumo'}</h3>
                <button onClick={() => { setShowAddForm(false); setEditItem(null); }} className="text-muted-color hover:text-white"><Icon name="XMarkIcon" size={20} /></button>
              </div>
              <form onSubmit={handleAdd} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-muted-color mb-1">Periodo Inizio *</label>
                    <input type="date" value={form.periodo_inizio} onChange={e => setForm(p => ({ ...p, periodo_inizio: e.target.value }))} required
                      className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-muted-color mb-1">Periodo Fine *</label>
                    <input type="date" value={form.periodo_fine} onChange={e => setForm(p => ({ ...p, periodo_fine: e.target.value }))} required
                      className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-muted-color mb-1">kWh * (&gt; 0)</label>
                    <input type="number" step="0.01" min="0.01" value={form.kwh} onChange={e => setForm(p => ({ ...p, kwh: e.target.value }))} required
                      className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-muted-color mb-1">Costo (€)</label>
                    <input type="number" step="0.01" min="0" value={form.costo_euro} onChange={e => setForm(p => ({ ...p, costo_euro: e.target.value }))}
                      className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-muted-color mb-1">Zona</label>
                    <select value={form.zona_id} onChange={e => setForm(p => ({ ...p, zona_id: e.target.value }))}
                      className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500">
                      <option value="">— Zona —</option>
                      {zone.map(z => <option key={z.id} value={z.id}>{z.nome}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-muted-color mb-1">Reparto</label>
                    <select value={form.reparto_id} onChange={e => setForm(p => ({ ...p, reparto_id: e.target.value }))}
                      className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500">
                      <option value="">— Reparto —</option>
                      {reparti.map(r => <option key={r.id} value={r.id}>{r.nome}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-color mb-1">Note</label>
                  <input value={form.note} onChange={e => setForm(p => ({ ...p, note: e.target.value }))}
                    className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-sky-500" />
                </div>
                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => { setShowAddForm(false); setEditItem(null); }} className="flex-1 px-4 py-2 border border-border text-secondary-color rounded-lg hover:bg-surface-elevated transition-colors text-sm">Annulla</button>
                  <button type="submit" disabled={saving} className="flex-1 px-4 py-2 bg-sky-500 text-white rounded-lg hover:bg-sky-600 transition-colors text-sm font-semibold disabled:opacity-50">
                    {saving ? 'Salvataggio...' : editItem ? 'Aggiorna' : 'Salva'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
